import apiService, { ApiError } from './apiService';
import { STORAGE_KEYS } from '../constants';

/**
 * Authentication API Service
 * Handles all authentication-related API calls
 */
class AuthService {
  constructor() {
    this.apiService = apiService;
    this.refreshPromise = null; // Prevent multiple refresh attempts
  }

  /**
   * Login user with email and password
   * @param {string} username - User email
   * @param {string} password - User password
   * @param {string|null} deviceId - The device ID for "Remember Me" functionality
   * @returns {Promise<Object>} Login response with user data and token
   */
  async login(username, password, deviceId) {
    try {
      const response = await this.apiService.skaiOneRequest(
        '/api/v1/account/skaix/login',
        {
          method: 'POST',
          data: {
            username,
            password,
            deviceId,
            app: 'admin-web',
          },
          includeAuth: false, // Don't include auth token for login
        }
      );

      console.log('Login response:', response);

      // Validate response structure for successful login
      if (!response || response.code !== 'OK') {
        throw new ApiError(
          response?.message || 'Login failed',
          400,
          response?.code || 'LOGIN_FAILED',
          response
        );
      }

      const userData = response.data;

      // Validate required fields
      if (!userData.token || !userData.id) {
        throw new ApiError(
          'Invalid login response: missing required fields',
          400,
          'INVALID_RESPONSE'
        );
      }

      // Store auth data immediately
      this.storeAuthData(userData);

      return {
        success: true,
        user: this.transformUserData(userData),
        token: userData.token,
        refreshToken: userData.token, // Using same token as refresh token
      };
    } catch (error) {
      if (error instanceof ApiError) {
        // Handle specific case where OTP verification is required
        if (error.code === 'AccountVerifyOtp') {
          console.log('OTP verification required, returning OTP flow response');
          return {
            success: false,
            requiresOtp: true,
            email: username,
            message: error.message || 'Please contact support',
            code: error.code,
            data: error?.data?.data,
          };
        }
        throw error;
      }

      // Handle network or other errors
      throw new ApiError(
        error.message ||
          'Login failed. Please check your connection and try again.',
        0,
        'NETWORK_ERROR'
      );
    }
  }

  /**
   * Send OTP to email for verification
   * @param {string} email - User email
   * @param {string} recaptchaResponse - Recaptcha response token
   * @param {string|null} deviceId - Optional device ID when "Remember Me" is checked
   * @returns {Promise<Object>} Send OTP response
   */
  async sendOtp(email, token, deviceId = null) {
    try {
      const response = await this.apiService.skaiOneRequest(
        '/api/v1/account/send-otp-email',
        {
          method: 'POST',
          data: {
            email,
            token,
            ...(deviceId && { deviceId }),
          },
          includeAuth: false,
        }
      );

      if (!response || response.code !== 'OK') {
        throw new ApiError(
          response?.message || 'Failed to send OTP',
          400,
          response?.code || 'SEND_OTP_FAILED',
          response
        );
      }

      return {
        success: true,
        message: response.message || 'OTP sent successfully',
      };
    } catch (error) {
      console.error('Send OTP error:');

      if (error instanceof ApiError) {
        throw error;
      }

      throw new ApiError(
        error.message || 'Failed to send OTP. Please try again.',
        0,
        'NETWORK_ERROR'
      );
    }
  }

  /**
   * Verify OTP for email verification
   * @param {string} email - User email
   * @param {string} otp - OTP code
   * @param {string|null} deviceId - The device ID for "Remember Me" functionality
   * @returns {Promise<Object>} Verify OTP response with user data and token
   */
  async verifyOtp(email, otp, deviceId) {
    try {
      const response = await this.apiService.skaiOneRequest(
        '/api/v1/account/verify-otp-email',
        {
          method: 'POST',
          data: {
            email,
            otp,
            deviceId,
          },
          includeAuth: false,
        }
      );

      // Validate response structure
      if (!response || response.code !== 'OK') {
        throw new ApiError(
          response?.message || 'OTP verification failed',
          400,
          response?.code || 'OTP_VERIFICATION_FAILED',
          response
        );
      }

      const userData = response.data;

      // Validate required fields
      if (!userData.token || !userData.id) {
        throw new ApiError(
          'Invalid OTP verification response: missing required fields',
          400,
          'INVALID_RESPONSE'
        );
      }

      // Store auth data immediately
      this.storeAuthData(userData);

      return {
        success: true,
        user: this.transformUserData(userData),
        token: userData.token,
        refreshToken: userData.token,
      };
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }

      throw new ApiError(
        error.message || 'OTP verification failed. Please try again.',
        0,
        'NETWORK_ERROR'
      );
    }
  }

  /**
   * Refresh authentication token
   * @returns {Promise<Object>} Refresh response with new token
   */
  async refreshToken() {
    // Prevent multiple concurrent refresh attempts
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    try {
      this.refreshPromise = this._performTokenRefresh();
      const result = await this.refreshPromise;
      return result;
    } finally {
      this.refreshPromise = null;
    }
  }

  /**
   * Internal method to perform token refresh
   * @private
   */
  async _performTokenRefresh() {
    try {
      const currentToken = this.apiService.getAuthToken();

      if (!currentToken) {
        throw new ApiError(
          'No refresh token available',
          401,
          'NO_REFRESH_TOKEN'
        );
      }

      const response = await this.apiService.skaiOneRequest(
        '/api/v1/account/refresh-token',
        {
          method: 'GET',
          includeAuth: true, // Include current token for refresh
        }
      );

      // Validate response structure
      if (!response || response.code !== 'OK') {
        throw new ApiError(
          response?.message || 'Token refresh failed',
          401,
          response?.code || 'REFRESH_FAILED',
          response
        );
      }

      const userData = response.data;

      // Validate required fields
      if (!userData.token) {
        throw new ApiError(
          'Invalid refresh response: missing token',
          401,
          'INVALID_REFRESH_RESPONSE'
        );
      }

      // Update stored auth data
      this.updateAuthData(userData);

      return {
        success: true,
        token: userData.token,
        tokenExpired: userData.tokenExpired,
        refreshTokenExpired: userData.refreshTokenExpired,
        user: this.transformUserData(userData),
      };
    } catch (error) {
      console.error('Token refresh error:');

      // Clear auth data on refresh failure
      this.clearAuthData();

      if (error instanceof ApiError) {
        throw error;
      }

      // Handle network or other errors
      throw new ApiError(
        'Session expired. Please log in again.',
        401,
        'REFRESH_FAILED'
      );
    }
  }

  /**
   * Logout user
   * @returns {Promise<Object>} Logout response
   */
  async logout() {
    try {
      // Clear local auth data
      this.clearAuthData();

      return { success: true };
    } catch (error) {
      console.error('Logout error:');

      // Always clear local data even if logout fails
      this.clearAuthData();

      return { success: true };
    }
  }

  /**
   * Get current user data
   * @returns {Promise<Object>} Current user data
   */
  async getCurrentUser() {
    try {
      // Try to get user from localStorage first
      const storedUser = this.getStoredUser();
      if (storedUser) {
        return {
          success: true,
          user: storedUser,
        };
      }

      // If no stored user, try to fetch from API (endpoint may not exist)
      try {
        const response = await this.apiService.skaiOneRequest(
          '/api/v1/account/profile',
          {
            method: 'GET',
            includeAuth: true,
          }
        );

        if (response && response.code === 'OK' && response.data) {
          const user = this.transformUserData(response.data);

          // Update stored user data
          this.updateStoredUser(user);

          return {
            success: true,
            user,
          };
        }
      } catch (error) {
        console.warn('Get current user API error:');
      }

      // If API call fails, return null (not authenticated)
      return {
        success: false,
        user: null,
      };
    } catch (error) {
      console.error('Get current user error:');
      return {
        success: false,
        user: null,
      };
    }
  }

  /**
   * Check if current token is expired or near expiration
   * @param {number} thresholdMinutes - Minutes before expiration to consider as expired
   * @returns {boolean} True if token should be refreshed
   */
  shouldRefreshToken(thresholdMinutes = 5) {
    try {
      const userData = this.getStoredUser();
      if (!userData || !userData.tokenExpired) {
        return false;
      }

      const tokenExpiry = new Date(userData.tokenExpired).getTime();
      const now = Date.now();
      const thresholdMs = thresholdMinutes * 60 * 1000;

      return tokenExpiry - now <= thresholdMs;
    } catch (error) {
      console.warn('Error checking token expiration:');
      return false;
    }
  }

  /**
   * Transform API user data to internal format
   * @param {Object} apiUserData - Raw user data from API
   * @returns {Object} Transformed user data
   */
  transformUserData(apiUserData) {
    return {
      id: apiUserData.id,
      name: apiUserData.name,
      email: apiUserData.email,
      phone: apiUserData.phone,
      hasPin: apiUserData.hasPin || false,
      roles: apiUserData.roles || {},
      token: apiUserData.token,
      tokenExpired: apiUserData.tokenExpired,
      refreshTokenExpired: apiUserData.refreshTokenExpired,
      avatar: null, // API doesn't provide avatar
      lastLoginAt: new Date().toISOString(),
    };
  }

  /**
   * Store authentication data in localStorage
   * @param {Object} userData - User data to store
   */
  storeAuthData(userData) {
    try {
      const transformedUser = this.transformUserData(userData);

      localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, userData.token);
      localStorage.setItem(
        STORAGE_KEYS.USER_DATA,
        JSON.stringify(transformedUser)
      );
      localStorage.setItem(STORAGE_KEYS.SESSION_START, Date.now().toString());
      localStorage.setItem(STORAGE_KEYS.LAST_ACTIVITY, Date.now().toString());

      // Store refresh token (using same token for now)
      if (userData.token) {
        localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, userData.token);
      }
    } catch (error) {
      console.error('Failed to store auth data:');
    }
  }

  /**
   * Update authentication data in localStorage
   * @param {Object} userData - Updated user data
   */
  updateAuthData(userData) {
    try {
      const existingUser = this.getStoredUser();
      const updatedUser = {
        ...existingUser,
        ...this.transformUserData(userData),
      };

      localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, userData.token);
      localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(updatedUser));
      localStorage.setItem(STORAGE_KEYS.LAST_ACTIVITY, Date.now().toString());

      // Update refresh token if provided
      if (userData.token) {
        localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, userData.token);
      }
    } catch (error) {
      console.error('Failed to update auth data:');
    }
  }

  /**
   * Update stored user data
   * @param {Object} userData - User data to update
   */
  updateStoredUser(userData) {
    try {
      const existingUser = this.getStoredUser();
      const updatedUser = {
        ...existingUser,
        ...userData,
      };

      localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(updatedUser));
    } catch (error) {
      console.error('Failed to update stored user:');
    }
  }

  /**
   * Get stored user data from localStorage
   * @returns {Object|null} Stored user data or null
   */
  getStoredUser() {
    try {
      const userData = localStorage.getItem(STORAGE_KEYS.USER_DATA);
      return userData ? JSON.parse(userData) : null;
    } catch (error) {
      console.error('Failed to get stored user:');
      return null;
    }
  }

  /**
   * Clear all authentication data from localStorage
   */
  clearAuthData() {
    try {
      localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
      localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
      localStorage.removeItem(STORAGE_KEYS.USER_DATA);
      localStorage.removeItem(STORAGE_KEYS.SESSION_START);
      localStorage.removeItem(STORAGE_KEYS.LAST_ACTIVITY);
      localStorage.removeItem(STORAGE_KEYS.SESSION_WARNING_SHOWN);
    } catch (error) {
      console.error('Failed to clear auth data:');
    }
  }

  /**
   * Check if user is authenticated
   * @returns {boolean} True if user is authenticated
   */
  isAuthenticated() {
    try {
      const token = this.apiService.getAuthToken();
      const user = this.getStoredUser();
      return !!(token && user && user.id);
    } catch (error) {
      console.error('Error checking authentication status:');
      return false;
    }
  }

  /**
   * Get remaining token time in milliseconds
   * @returns {number} Remaining time in milliseconds
   */
  getRemainingTokenTime() {
    try {
      const user = this.getStoredUser();
      if (!user || !user.tokenExpired) {
        return 0;
      }

      const tokenExpiry = new Date(user.tokenExpired).getTime();
      const now = Date.now();
      return Math.max(0, tokenExpiry - now);
    } catch (error) {
      console.warn('Error calculating remaining token time:');
      return 0;
    }
  }
}

// Create singleton instance
const authService = new AuthService();

export default authService;
